import { deleteSubscription } from '@commenda/api';
import { Dialog, DialogContent } from '@commenda/kakashi';
import React, { useState } from 'react';
import { useNotification } from '../../hooks/useNotification';
import { CancellationSuccessStep } from './steps';
import { CancellationReasonStep } from './steps/CancellationReasonStep';
import { CancelSubscriptionStep } from './steps/CancelSubscriptionStep';

export type SubscriptionModalStep = 'cancel' | 'reason' | 'success';

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialStep: SubscriptionModalStep;
  title: string;
  nextPaymentAmount: number;
  nextPaymentDate: string;
  planNames: string[];
  onCancelSubscription?: (reason: string) => void;
  companyId?: number;
  subscriptionId?: string;
  onSubscriptionDeleted?: () => void;
}

export const SubscriptionModal = ({
  isOpen,
  onClose,
  initialStep,
  title,
  nextPaymentAmount,
  nextPaymentDate,
  planNames,
  onCancelSubscription,
  companyId,
  subscriptionId,
  onSubscriptionDeleted,
}: SubscriptionModalProps) => {
  const [step, setStep] = useState<SubscriptionModalStep>(initialStep);
  const [isLoading, setIsLoading] = useState(false);
  const { setNotification } = useNotification();

  const handleConfirmCancellation = (reason: string) => {
    if (!companyId || !subscriptionId) {
      onCancelSubscription?.(reason);
      setStep('success');
    }
  };

  const handleCloseModal = () => {
    setStep(initialStep);
    setIsLoading(false);
    onClose();
  };

  const handleApiCancellation = async (reason: string) => {
    if (!companyId || !subscriptionId) {
      handleConfirmCancellation(reason);
      return;
    }

    setIsLoading(true);

    try {
      await deleteSubscription({ companyId, subscriptionId });
      setStep('success');
      setNotification({
        show: true,
        valid: true,
        title: 'Subscription Cancelled',
        message: 'Your subscription has been successfully cancelled.',
      });
      onSubscriptionDeleted?.();
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      setNotification({
        show: true,
        valid: false,
        title: 'Unable to Cancel Subscription',
        message:
          'We are unable to process this request at the moment. Please try again or contact support.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      setStep(initialStep);
      setIsLoading(false);
    }
  }, [isOpen, initialStep]);

  let jsx = null;
  switch (step) {
    case 'cancel':
      jsx = (
        <CancelSubscriptionStep
          onClose={handleCloseModal}
          onConfirm={() => setStep('reason')}
          title={title}
          nextPaymentAmount={nextPaymentAmount}
          nextPaymentDate={nextPaymentDate}
          planNames={planNames}
        />
      );
      break;
    case 'reason':
      jsx = (
        <CancellationReasonStep
          onClose={handleCloseModal}
          onConfirm={
            companyId && subscriptionId
              ? handleApiCancellation
              : handleConfirmCancellation
          }
          type="subscription"
          isLoading={isLoading}
        />
      );
      break;
    case 'success':
      jsx = (
        <CancellationSuccessStep
          onClose={handleCloseModal}
          name={title}
          expiryDate={nextPaymentDate}
          type="subscription"
        />
      );
      break;
    default:
      break;
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleCloseModal}>
      <DialogContent className="max-w-none w-fit">{jsx}</DialogContent>
    </Dialog>
  );
};

export default SubscriptionModal;
